import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { LoggerService } from '@libraries/logger';
import loadjs from 'loadjs';
import {
  FastlinkWindow,
  YodleeAccountStatus,
  YodleeEvent,
  YodleeLoadModule,
  YodleeParams,
  YodleeResult,
} from '../models';
import { YodleeService } from '../services/yodlee.service';
import { YodleeConfig } from '../yodlee.config';

declare const window: FastlinkWindow;

@Component({
  selector: 'yodlee-fastlink',
  templateUrl: './yodlee-fastlink.component.html',
  styleUrls: ['./yodlee-fastlink.component.scss'],
  standalone: false,
})
export class YodleeFastlinkComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() token!: string;
  @Input() yodlee!: YodleeParams;
  @Output() done: EventEmitter<YodleeResult[]> = new EventEmitter();

  fastlinkElementId = 'container-fastlink';
  fastlinkScriptId = 'fastlink-script';
  fastlinkScriptUrl = 'https://cdn.yodlee.com/fastlink/v3/initialize.js';

  constructor(
    private config: YodleeConfig,
    private logger: LoggerService,
    private yodleeService: YodleeService
  ) {}

  ngOnInit() {
    if (!this.token) {
      throw new Error('@Input() token not found');
    }
    if (!this.yodlee) {
      throw new Error('@Input() yodlee not found');
    }
  }

  ngAfterViewInit() {
    // when closing and re-opening the iframe, it needs to be initialized in ngAfterViewInit to give template enough time to instantiate container-fastlink DOM element
    if (this.canLoadYodlee) {
      this.loadYodlee();
    }
  }

  ngOnDestroy() {
    window.fastlink.close();
    this.logEvent({ event: 'closed' }, true);
  }

  get canLoadYodlee(): boolean {
    return this.yodlee?.jwt || this.yodlee?.token ? true : false;
  }

  loadYodlee() {
    if (!loadjs.isDefined(this.fastlinkScriptId)) {
      loadjs(this.fastlinkScriptUrl, this.fastlinkScriptId, {
        returnPromise: true,
      }).catch((path: string) => {
        throw new Error(`Failed to load Yodlee Fastlink Script ${path}`);
      });
    }
    loadjs.ready(this.fastlinkScriptId, () => {
      this.openYodlee();
    });
  }

  openYodlee() {
    window.fastlink.open(
      {
        forceIframe: true,
        fastLinkURL: this.config.fastlinkUrl,
        accessToken: this.yodlee.token ? `Bearer ${this.yodlee.token}` : undefined,
        jwtToken: this.yodlee.jwt || undefined,
        onSuccess: (data: YodleeAccountStatus) => {
          this.logger.log('onSuccess', data);
          this.accountStatus(data);
        },
        onError: (data: YodleeAccountStatus) => {
          this.logger.log('onError', data);
          this.accountStatus(data);
        },
        onExit: (data: YodleeAccountStatus) => {
          this.logger.log('onExit', data);
          this.done.emit(data.sites);
        },
        onEvent: (data: YodleeLoadModule) => {
          this.logger.log('onEvent', data);
          this.loadModule(data);
        },
      },
      this.fastlinkElementId
    );
    this.logEvent({ event: 'loaded' });
  }

  logEvent(event: YodleeEvent, async = true) {
    this.logger.log('logEvent', event);
    this.yodleeService.updateLog(this.token, event, async);
  }

  // event name from yodlee iframe
  accountStatus(data: YodleeAccountStatus) {
    if (data.action === 'exit') return;
    const { bankName, reason, requestId, status, statusCode, additionalStatus } = data;
    this.logEvent({
      event: 'accountStatus',
      bankName: bankName,
      reason: reason || null,
      requestId,
      status: status,
      statusCode: statusCode || additionalStatus || null,
    });
  }

  // event name from yodlee iframe
  loadModule(data: YodleeLoadModule) {
    if (data.finappId) {
      // sometimes yodlee sends us finappId inside an array, other times as string
      const id = Array.isArray(data.finappId) ? data.finappId[0] : data.finappId;
      const step = `step_${id[id.length - 1]}`;
      this.logEvent({ event: step });
    }
  }

  @HostListener('window:beforeunload') onBeforeUnload() {
    this.logEvent({ event: 'window_closed' }, false);
  }
}
